import { supabase } from '@/integrations/supabase/client';
import { v4 as uuidv4 } from 'uuid';
import {
  createLearnerFieldSaveCoordinator,
  type LearnerFieldSaveCommitError,
  type LearnerFieldSaveCoordinator,
  type LearnerFieldSaveCoordinatorDependencies,
  type LearnerFieldSaveMutation,
  type LearnerFieldSaveStateChange,
} from './learnerFieldSaveCoordinator';

export interface SaveFieldOptions {
  userId?: string;
  id: string;
  promptModuleId: string;
  stepId: string;
  roadmapId: string;
  templateId?: string;
  instanceId?: string;
  value: string;
  valueType?: 'text' | 'interactive_prompt' | 'quiz' | 'matching_quiz' | 'interactive_exercise' | 'react_activity' | 'ai_companion_chat' | 'google_business_profile' | 'media_display' | 'video_player';
  metadata?: Record<string, unknown>;
  included?: boolean;
  flushImmediately?: boolean;
  isUpdate?: boolean;  // NEW: Flag to preserve original user_id
  originalUserId?: string;  // NEW: Original owner for brand sharing updates
  gradingResponse?: Record<string, unknown>;
}

export interface SaveResult {
  success: boolean;
  error?: string;
  data?: unknown;
}

interface FieldResponse {
  value: string;
  valueType?: string;
  metadata?: Record<string, unknown>;
  promptModuleId?: string;
  updatedAt: string;
  included?: boolean;
}

interface GetFieldResponsesParams {
  userId?: string;
  stepId?: string;
  roadmapId: string;
  promptModuleId?: string;
  instanceId?: string;
}

interface DatabaseFieldResponse {
  id: string;
  user_id: string;
  step_id: string;
  roadmap_id: string;
  prompt_module_id: string;
  value: string;
  value_type: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string | null;
  updated_at: string | null;
  included: boolean | null;
  instance_id: string | null;
  // Optional JSON column used by some graders
  grading_response?: Record<string, unknown>;
}

export type AtomicLearnerFieldSaveOptions = SaveFieldOptions & {
  userId: string;
  valueType: 'text' | 'interactive_prompt';
  metadata: Record<string, unknown> & {
    tag: 'text' | 'textarea' | 'interactive_prompt';
    sourceFieldId: string;
  };
};

export interface AtomicLearnerFieldAcknowledgement {
  id: string;
  mutationId: string;
  version: number;
}

export type AtomicLearnerFieldSaveResult =
  | {
    success: true;
    disposition: 'current' | 'superseded';
    data: AtomicLearnerFieldAcknowledgement;
  }
  | {
    success: false;
    disposition: 'failed';
    error: string;
  };

interface AtomicRpcClient {
  rpc(name: string, args: Record<string, unknown>): PromiseLike<unknown>;
}

type AtomicCoordinatorOptions = Partial<Omit<
  LearnerFieldSaveCoordinatorDependencies<
    AtomicLearnerFieldSaveOptions,
    AtomicLearnerFieldAcknowledgement
  >,
  'commit'
>>;

interface UserResponseServiceDependencies {
  atomicRpcClient?: AtomicRpcClient;
  learnerFieldCoordinator?: AtomicCoordinatorOptions;
  getCurrentAuthUserId?: () => Promise<string | null>;
}

class UserResponseService {
  private saveQueue: Map<string, SaveFieldOptions> = new Map();
  private saveTimeout: NodeJS.Timeout | null = null;
  private isOnline = navigator.onLine;
  private anonymousToken: string | null = null;
  private readonly atomicRpcClient: AtomicRpcClient;
  private readonly getCurrentAuthUserId: () => Promise<string | null>;
  private readonly atomicLearnerFieldCoordinator: LearnerFieldSaveCoordinator<
    AtomicLearnerFieldSaveOptions,
    AtomicLearnerFieldAcknowledgement
  >;

  constructor(dependencies: UserResponseServiceDependencies = {}) {
    this.atomicRpcClient = dependencies.atomicRpcClient ?? {
      rpc: (name, args) => supabase.rpc(name, args),
    };
    this.getCurrentAuthUserId = dependencies.getCurrentAuthUserId ?? (async () => {
      const { data, error } = await supabase.auth.getSession();
      if (error) throw error;
      return data.session?.user.id ?? null;
    });
    this.atomicLearnerFieldCoordinator = createLearnerFieldSaveCoordinator({
      ...dependencies.learnerFieldCoordinator,
      createMutationId: dependencies.learnerFieldCoordinator?.createMutationId ?? uuidv4,
      commit: (mutation) => this.commitAtomicLearnerFieldResponse(mutation),
    });

    window.addEventListener('online', () => {
      this.isOnline = true;
      this.processSaveQueue();
      this.retryFailedAtomicLearnerFieldSaves();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
    });

    window.addEventListener('focus', () => {
      this.retryFailedAtomicLearnerFieldSaves();
    });

    window.addEventListener('beforeunload', (event) => {
      if (!this.hasPendingAtomicLearnerFieldSaves()) return;
      event.preventDefault();
      event.returnValue = '';
    });

    this.initAnonymousToken();
  }

  private atomicLearnerFieldKey(options: AtomicLearnerFieldSaveOptions) {
    return JSON.stringify([
      options.userId,
      options.instanceId ?? null,
      options.stepId,
      options.promptModuleId,
      options.metadata.sourceFieldId,
    ]);
  }

  private async commitAtomicLearnerFieldResponse(
    mutation: LearnerFieldSaveMutation<AtomicLearnerFieldSaveOptions>,
  ): Promise<AtomicLearnerFieldAcknowledgement> {
    const options = mutation.payload;
    let currentAuthUserId: string | null;
    try {
      currentAuthUserId = await this.getCurrentAuthUserId();
    } catch {
      throw { kind: 'unknown' } satisfies LearnerFieldSaveCommitError;
    }
    if (currentAuthUserId !== options.userId) {
      throw { kind: 'unknown' } satisfies LearnerFieldSaveCommitError;
    }

    let response: unknown;
    try {
      response = await this.atomicRpcClient.rpc('save_atomic_learner_field_response', {
        p_instance_id: options.instanceId ?? null,
        p_step_id: options.stepId,
        p_prompt_module_id: options.promptModuleId,
        p_roadmap_id: options.roadmapId,
        p_template_id: options.templateId ?? null,
        p_field_key: options.metadata.sourceFieldId,
        p_value: options.value,
        p_value_type: options.valueType,
        p_metadata: options.metadata,
        p_grading_response: options.gradingResponse ?? null,
        p_mutation_id: mutation.mutationId,
      });
    } catch {
      throw { kind: 'network' } satisfies LearnerFieldSaveCommitError;
    }

    if (typeof response !== 'object' || response === null) {
      throw { kind: 'unknown' } satisfies LearnerFieldSaveCommitError;
    }
    const result = response as { data?: unknown; error?: unknown; status?: unknown };
    if (result.error) {
      if (result.status === 0) {
        throw { kind: 'network' } satisfies LearnerFieldSaveCommitError;
      }
      if (typeof result.status === 'number') {
        throw {
          kind: 'http',
          status: result.status,
        } satisfies LearnerFieldSaveCommitError;
      }
      throw { kind: 'unknown' } satisfies LearnerFieldSaveCommitError;
    }

    if (!Array.isArray(result.data) || result.data.length !== 1) {
      throw { kind: 'unknown' } satisfies LearnerFieldSaveCommitError;
    }
    const acknowledgement = result.data[0] as Record<string, unknown> | null;
    if (
      acknowledgement === null
      || typeof acknowledgement !== 'object'
      || typeof acknowledgement.response_id !== 'string'
      || acknowledgement.response_id.trim() === ''
      || acknowledgement.mutation_id !== mutation.mutationId
      || typeof acknowledgement.response_version !== 'number'
      || !Number.isInteger(acknowledgement.response_version)
    ) {
      throw { kind: 'unknown' } satisfies LearnerFieldSaveCommitError;
    }

    const parsedAcknowledgement = {
      id: acknowledgement.response_id,
      mutationId: acknowledgement.mutation_id,
      version: acknowledgement.response_version,
    };
    return parsedAcknowledgement;
  }

  async saveAtomicLearnerFieldResponse(
    options: AtomicLearnerFieldSaveOptions,
  ): Promise<AtomicLearnerFieldSaveResult> {
    const ticket = this.atomicLearnerFieldCoordinator.enqueue({
      key: this.atomicLearnerFieldKey(options),
      payload: options,
    });
    try {
      const acknowledgement = await ticket.acknowledgement;
      const disposition = ticket.mutationId === acknowledgement.mutationId
        ? 'current'
        : 'superseded';
      if (disposition === 'current') {
        try {
          window.dispatchEvent(new CustomEvent('user-field-response-saved', {
            detail: {
              instanceId: options.instanceId,
              fieldId: options.promptModuleId,
              valueType: options.valueType,
              responseId: acknowledgement.id,
            },
          }));
        } catch {
          // Cache invalidation is best-effort after the accepted acknowledgement.
        }
      }
      return { success: true, disposition, data: acknowledgement };
    } catch (error) {
      const typedError = error as LearnerFieldSaveCommitError;
      return {
        success: false,
        disposition: 'failed',
        error: typedError.kind === 'http'
          ? `Learner field save failed with HTTP ${typedError.status ?? 'unknown'}`
          : `Learner field save failed: ${typedError.kind ?? 'unknown'}`,
      };
    }
  }

  getAtomicLearnerFieldSaveState(
    options: AtomicLearnerFieldSaveOptions,
  ): LearnerFieldSaveStateChange | undefined {
    return this.atomicLearnerFieldCoordinator.getState(this.atomicLearnerFieldKey(options));
  }

  retryFailedAtomicLearnerFieldSaves() {
    return this.atomicLearnerFieldCoordinator.retryFailed();
  }

  hasPendingAtomicLearnerFieldSaves() {
    return this.atomicLearnerFieldCoordinator.hasPending();
  }

  resetAtomicLearnerFieldSavesForAuthChange() {
    this.atomicLearnerFieldCoordinator.reset({ kind: 'unknown' });
  }

  private async initAnonymousToken() {
    const stored = localStorage.getItem('anon_session_token');
    if (stored) {
      this.anonymousToken = stored;
    } else {
      this.anonymousToken = uuidv4();
      localStorage.setItem('anon_session_token', this.anonymousToken);
    }
  }

  async getFieldResponses(params: GetFieldResponsesParams): Promise<DatabaseFieldResponse[]> {
    try {
      console.log('userResponseService: Loading field responses for:', params);

      let query = supabase
        .from('user_field_responses')
        .select('*')
        .eq('roadmap_id', params.roadmapId);

      // Add userId filter for instructor viewing mode
      // This allows instructors to view specific student responses
      if (params.userId && params.userId.trim() !== '') {
        query = query.eq('user_id', params.userId);
      }
      // Note: When userId is not provided, RLS policies handle access control for shared brand records

      // Only add stepId filter if it's provided and not empty
      if (params.stepId && params.stepId.trim() !== '') {
        query = query.eq('step_id', params.stepId);
      }

      // Only add promptModuleId filter if it's provided and not empty
      if (params.promptModuleId && params.promptModuleId.trim() !== '') {
        query = query.eq('prompt_module_id', params.promptModuleId);
      }

      // Only add instanceId filter if it's provided and not empty
      if (params.instanceId && params.instanceId.trim() !== '') {
        query = query.eq('instance_id', params.instanceId);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading field responses:', error);
        throw error;
      }

      console.log(`userResponseService: Loaded ${data?.length || 0} field responses`);
      return data || [];
    } catch (error) {
      console.error('Error in getFieldResponses:', error);
      throw error;
    }
  }

  async getFieldResponseById(id: string): Promise<DatabaseFieldResponse | null> {
    try {
      console.log('userResponseService: Loading field response by ID:', id);

      const { data, error } = await supabase
        .from('user_field_responses')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error('userResponseService: Error loading field response by ID:', error);
        return null;
      }

      console.log('userResponseService: Loaded field response by ID:', data);
      return data;
    } catch (error) {
      console.error('userResponseService: Exception loading field response by ID:', error);
      return null;
    }
  }

  async deleteFieldResponse(id: string): Promise<SaveResult> {
    try {
      console.log('userResponseService: Deleting field response with ID:', id);

      const { error } = await supabase
        .from('user_field_responses')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('userResponseService: Error deleting field response:', error);
        return { success: false, error: error.message };
      }

      console.log('userResponseService: Field response deleted successfully');
      return { success: true };
    } catch (error) {
      console.error('userResponseService: Exception deleting field response:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  async saveFieldResponse(options: SaveFieldOptions): Promise<SaveResult> {
    // For text fields and interactive prompts, find existing record by context.
    if ((options.valueType === 'text' || options.valueType === 'interactive_prompt') && options.metadata?.sourceFieldId) {
      try {
        console.log('🔍 FIELD_SAVE_LOOKUP: Searching for existing records', {
          userId: options.userId,
          stepId: options.stepId,
          promptModuleId: options.promptModuleId,
          instanceId: options.instanceId || options.roadmapId,
          sourceFieldId: options.metadata.sourceFieldId,
          providedId: options.id
        });

        // Fetch all matching records and filter by sourceFieldId in JS
        // (JSONB .contains() was silently failing)
        const { data: existingRecords } = await supabase
          .from('user_field_responses')
          .select('id, included, metadata, value, created_at')
          .eq('user_id', options.userId)
          .eq('step_id', options.stepId)
          .eq('prompt_module_id', options.promptModuleId)
          .eq('instance_id', options.instanceId || options.roadmapId)
          .in('value_type', ['text', 'interactive_prompt'])
          .eq('included', true);

        // Filter by sourceFieldId in JavaScript since JSONB query was failing
        const matchingRecords = (existingRecords || []).filter(r => {
          const metadata = r.metadata as Record<string, unknown> | null;
          return metadata?.sourceFieldId === options.metadata?.sourceFieldId;
        });

        console.log('🔍 FIELD_SAVE_LOOKUP: Found existing records', {
          totalFetched: existingRecords?.length || 0,
          matchingSourceFieldId: matchingRecords.length,
          records: matchingRecords.map(r => ({
            id: r.id,
            included: r.included,
            tag: (r.metadata as any)?.tag,
            valuePreview: r.value?.substring(0, 50),
            createdAt: r.created_at
          }))
        });

        // Reuse existing active record ID.
        const activeRecord = matchingRecords.find(record => record.included === true);
        if (activeRecord?.id) {
          console.log('📝 FIELD_SAVE: Reusing existing record ID:', {
            foundId: activeRecord.id,
            providedId: options.id,
            willOverride: activeRecord.id !== options.id
          });
          options.id = activeRecord.id;
        } else {
          console.log('📝 FIELD_SAVE: No existing record found, using provided ID:', options.id);
        }
      } catch (error) {
        console.warn('Warning: Could not check for existing record, proceeding with new ID', error);
      }
    }

    const key = `${options.id}-${options.promptModuleId}`;

    this.saveQueue.set(key, options);

    // If flushImmediately is true, process the queue now
    if (options.flushImmediately) {
      // Correctly await the processing and return its result
      return await this.processSaveQueue();
    }

    if (!this.isOnline) {
      this.saveToLocalStorage(options);
      return { success: true, data: { offline: true } };
    }

    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }

    this.saveTimeout = setTimeout(() => {
      this.processSaveQueue();
    }, 1000);

    return { success: true };
  }

  private async processSaveQueue(): Promise<SaveResult> {
    if (!this.isOnline || this.saveQueue.size === 0) {
      return { success: true, data: { status: 'no-op' } };
    }

    const itemsToSave = Array.from(this.saveQueue.values());
    this.saveQueue.clear();

    // Using Promise.all to wait for all saves to complete
    const savePromises = itemsToSave.map(item => {
      if (item.userId) {
        return this.saveAuthenticatedResponse(item);
      } else {
        return this.saveAnonymousResponse(item);
      }
    });

    try {
      const savedIds = await Promise.all(savePromises);
      console.log('✅ processSaveQueue: All items saved successfully.');
      // Return the first saved ID (for single-item saves)
      return { success: true, data: { id: savedIds[0] } };
    } catch (error) {
      console.error('❌ processSaveQueue: One or more items failed to save.', error);
      // Note: This simplified logic doesn't re-queue failed items.
      // For this specific use case, failing fast is acceptable.
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  private async saveAuthenticatedResponse(options: SaveFieldOptions) {
    // Ensure legacy metadata keys are present for backward compatibility
    const enforcedMetadata: Record<string, unknown> = {
      // Preserve any existing metadata fields first
      ...(options.metadata ?? {}),
      // Always enforce these legacy keys so older readers can find the record
      instanceId:
        options.instanceId ??
        (options.metadata as { instanceId?: string } | undefined)?.instanceId,
      templateId:
        options.templateId ??
        (options.metadata as { templateId?: string } | undefined)?.templateId,
      // Historically many readers look at `type` instead of `value_type`
      type: options.valueType ?? (options.metadata as { type?: string } | undefined)?.type,
    };

    console.log('💾 userResponseService: Saving authenticated response with enforced metadata:', {
      ...options,
      metadata: enforcedMetadata,
    });

    // FIXED: Preserve original user_id for brand sharing updates
    const upsertData: {
      id: string;
      prompt_module_id: string;
      step_id: string;
      roadmap_id: string;
      template_id?: string;
      instance_id?: string;
      value: string;
      value_type: string;
      metadata: Record<string, unknown>;
      included: boolean;
      updated_at: string;
      user_id?: string;
      grading_response?: Record<string, unknown>;
    } = {
      id: options.id,
      prompt_module_id: options.promptModuleId,
      step_id: options.stepId,
      roadmap_id: options.roadmapId,
      template_id: options.templateId,
      instance_id: options.instanceId,
      value: options.value,
      value_type: options.valueType || 'text',
      metadata: enforcedMetadata,
      included: options.included !== undefined ? options.included : true,
      updated_at: new Date().toISOString(),
      grading_response: options.gradingResponse
    };

    // Only set user_id for NEW records, preserve original owner for updates
    if (!options.isUpdate) {
      upsertData.user_id = options.userId;
    } else if (options.originalUserId) {
      upsertData.user_id = options.originalUserId;  // Preserve original owner
    }

    console.log('💾 userResponseService: Upsert data being sent to Supabase:', upsertData);

    // Use returning:'minimal' to avoid RLS SELECT issues when upserting; cast to any to bypass type mismatch
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const supabaseAny: any = supabase;
    const { data, error } = await supabaseAny
      .from('user_field_responses')
      .upsert(upsertData, { onConflict: 'id' })
      .select('id')
      .single();

    if (error) {
      console.error('Error saving user response:', error);
      throw error;
    }

    return data.id;
  }

  private async saveAnonymousResponse(options: SaveFieldOptions) {
    if (!this.anonymousToken) return;

    // Ensure legacy metadata keys are present for backward compatibility
    const enforcedMetadata: Record<string, unknown> = {
      ...(options.metadata ?? {}),
      instanceId:
        options.instanceId ??
        (options.metadata as { instanceId?: string } | undefined)?.instanceId,
      templateId:
        options.templateId ??
        (options.metadata as { templateId?: string } | undefined)?.templateId,
      type: options.valueType ?? (options.metadata as { type?: string } | undefined)?.type,
    };

    let existingFieldResponses: Record<string, unknown> = {};
    try {
      const getRes = await fetch(`/api/anonymous-sessions/${this.anonymousToken}`);
      if (getRes.ok) {
        const json = await getRes.json();
        existingFieldResponses = json.field_responses ?? {};
      }
      // 404 → no session yet; start with empty responses
    } catch (err) {
      console.error('[AnonymousSession] GET failed:', err);
    }

    const fieldKey = `${options.roadmapId}:${options.stepId}:${options.id}`;
    const responses = existingFieldResponses;

    responses[fieldKey] = {
      value: options.value,
      valueType: options.valueType,
      metadata: enforcedMetadata,
      promptModuleId: options.promptModuleId,
      templateId: options.templateId,
      instanceId: options.instanceId,
      included: options.included !== undefined ? options.included : true,
      updatedAt: new Date().toISOString()
    };

    try {
      await fetch(`/api/anonymous-sessions/${this.anonymousToken}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ field_responses: responses }),
      });
    } catch (err) {
      console.error('[AnonymousSession] PUT failed:', err);
    }

    // 🔔 Dispatch generic reload event for anonymous saves as well
    if (typeof window !== 'undefined') {
      try {
        window.dispatchEvent(new CustomEvent('user-field-response-saved', {
          detail: { instanceId: options.instanceId, fieldId: options.promptModuleId, valueType: options.valueType }
        }));
      } catch (_) {/* ignore */ }
    }
  }

  private async saveLargeContent(responseId: string, content: string) {
    await supabase
      .from('user_field_large_content')
      .insert({
        response_id: responseId,
        content: content,
        content_hash: await this.hashContent(content)
      });
  }

  private async hashContent(content: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(content);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  private saveToLocalStorage(options: SaveFieldOptions) {
    const key = `field_response_${options.roadmapId}_${options.stepId}_${options.id}`;
    const data = {
      ...options,
      savedAt: new Date().toISOString(),
      syncPending: true
    };
    localStorage.setItem(key, JSON.stringify(data));
  }

  async loadResponses(userId: string | null, roadmapId: string): Promise<Record<string, string>> {
    const responses: Record<string, string> = {};

    const localKeys = Object.keys(localStorage).filter(key =>
      key.startsWith(`field_response_${roadmapId}_`)
    );

    localKeys.forEach(key => {
      try {
        const data = JSON.parse(localStorage.getItem(key) || '{}');
        const fieldKey = `${data.stepId}:${data.id}`;
        responses[fieldKey] = data.value;
      } catch (e) {
        console.error('Failed to parse local response:', e);
      }
    });

    if (userId) {
      console.log('Loading responses for user:', userId, 'roadmap:', roadmapId);

      const { data, error } = await supabase
        .from('user_field_responses')
        .select('*')
        .eq('user_id', userId)
        // Load rows written under either the template (roadmap_id) or the current instance
        .or(`roadmap_id.eq.${roadmapId},instance_id.eq.${roadmapId}`);

      if (error) {
        console.error('Error loading user responses:', error);
      } else if (data) {
        console.log('Loaded user responses from database:', data);
        data.forEach(response => {
          const fieldKey = `${response.step_id}:${response.id}`;
          responses[fieldKey] = response.value;
        });
      }
    } else if (this.anonymousToken) {
      try {
        const getRes = await fetch(`/api/anonymous-sessions/${this.anonymousToken}`);
        if (getRes.ok) {
          const json = await getRes.json();
          const fieldResponses: Record<string, unknown> = json.field_responses ?? {};
          Object.entries(fieldResponses).forEach(([key, value]) => {
            if (key.startsWith(roadmapId)) {
              const [, stepId, id] = key.split(':');
              const fieldResponse = value as FieldResponse;
              responses[`${stepId}:${id}`] = fieldResponse.value;
            }
          });
        }
        // 404 → no session yet; leave responses empty
      } catch (err) {
        console.error('[AnonymousSession] GET failed during loadResponses:', err);
      }
    }

    console.log('Final loaded responses:', responses);
    return responses;
  }

  async migrateAnonymousToUser(userId: string) {
    if (!this.anonymousToken) return;

    try {
      const getRes = await fetch(`/api/anonymous-sessions/${this.anonymousToken}`);
      let fieldResponses: Record<string, unknown> = {};
      if (getRes.ok) {
        const json = await getRes.json();
        fieldResponses = json.field_responses ?? {};
      } else if (getRes.status !== 404) {
        console.error('[AnonymousSession] GET failed during migration, status:', getRes.status);
        return;
      }
      // 404 → no session to migrate; still clean up local token below

      if (Object.keys(fieldResponses).length > 0) {
        const responses = Object.entries(fieldResponses).map(([key, value]) => {
          const [roadmapId, stepId, id] = key.split(':');
          const fieldResponse = value as FieldResponse;
          return {
            user_id: userId,
            roadmap_id: roadmapId,
            step_id: stepId,
            id: id,
            prompt_module_id: fieldResponse.promptModuleId || 'unknown',
            value: fieldResponse.value,
            value_type: fieldResponse.valueType || 'text',
            metadata: fieldResponse.metadata,
            included: fieldResponse.included !== undefined ? fieldResponse.included : true
          };
        });

        if (responses.length > 0) {
          await supabase
            .from('user_field_responses')
            .upsert(responses, {
              onConflict: 'id'
            });
        }
      }

      await fetch(`/api/anonymous-sessions/${this.anonymousToken}`, { method: 'DELETE' });

      localStorage.removeItem('anon_session_token');
      this.anonymousToken = null;
    } catch (error) {
      console.error('Failed to migrate anonymous data:', error);
    }
  }

  async syncLocalStorage() {
    if (!this.isOnline) return;

    const localKeys = Object.keys(localStorage).filter(key =>
      key.startsWith('field_response_') && key.includes('syncPending')
    );

    for (const key of localKeys) {
      try {
        const data = JSON.parse(localStorage.getItem(key) || '{}');
        if (data.syncPending) {
          await this.saveFieldResponse(data);
          delete data.syncPending;
          localStorage.setItem(key, JSON.stringify(data));
        }
      } catch (e) {
        console.error('Failed to sync local response:', e);
      }
    }
  }
}

export function createUserResponseService(dependencies: UserResponseServiceDependencies = {}) {
  return new UserResponseService(dependencies);
}

export const userResponseService = createUserResponseService();
